import type { ProductCardDto } from '@runnerboy/types';
import { Alert, Button, cx, EmptyState, Input, Packshot, Skeleton } from '@runnerboy/ui';
import { ArrowLeft, BadgeCheck, Clock, Search, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { AdBanner, SponsoredProducts, useAds } from '../components/ads';
import { StorefrontArt } from '../components/art';
import { CartConflictSheet, ProductCard, stageStyle, useCartActions } from '../components/commerce';
import { AgeGateSheet } from '../components/sheets';
import { Page, ResponsibleNote } from '../components/shell';
import { client, useAsync, useSession } from '../lib/session';

interface StoreDetail {
  id: string;
  name: string;
  legalEntityName: string;
  locality: string;
  isOpen: boolean;
  closesAt: string | null;
  rating: number | null;
  isDemo: boolean;
  sellerOfRecord: { legalName: string; licenceVerified: boolean; licenceNumberMasked: string | null };
}
interface Catalog {
  items: ProductCardDto[];
  nextCursor: string | null;
  alcoholBrowsable: boolean;
  normalizedQuery: string | null;
}
interface Category {
  slug: string;
  name: string;
  productType: string;
}

export function StorePage({ searchMode = false }: { searchMode?: boolean }) {
  const { t } = useTranslation();
  const params = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { storeId: selectedStore, setStoreId, ageGate } = useSession();
  const storeId = params.storeId ?? selectedStore;
  const category = sp.get('category') ?? '';
  const [q, setQ] = useState(sp.get('q') ?? '');
  const [debounced, setDebounced] = useState(q);
  const [extra, setExtra] = useState<ProductCardDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [ageOpen, setAgeOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(searchMode || !!q);
  const updateQuery = (v: string) => {
    setQ(v);
    setSp(
      (p) => {
        const n = new URLSearchParams(p);
        if (v) n.set('q', v);
        else n.delete('q');
        return n;
      },
      { replace: true },
    );
  };
  const closeSearch = () => {
    updateQuery('');
    setSearchOpen(false);
  };
  const actions = useCartActions();
  const ads = useAds(['STORE_BANNER', 'CATEGORY_BANNER', 'SPONSORED_PRODUCTS'], { storeId, category: category || null, enabled: !!storeId && !searchMode });
  const browsingHome = !searchMode && !category && !debounced;

  useEffect(() => {
    if (storeId && storeId !== selectedStore) setStoreId(storeId);
  }, [storeId, selectedStore, setStoreId]);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    if (ageGate === null) setAgeOpen(true);
  }, [ageGate]);

  const store = useAsync((s) => client.get<StoreDetail>(`/public/stores/${storeId}`, s), [storeId], { enabled: !!storeId });
  const categories = useAsync((s) => client.get<Category[]>('/public/categories', s), []);
  // Bring the selected aisle chip into view (horizontal only; never moves the page).
  const chipsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = chipsRef.current;
    const chip = row?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (row && chip) row.scrollTo({ left: chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2, behavior: 'smooth' });
  }, [category, searchOpen, categories.data]);
  const qs = new URLSearchParams({ limit: '40', ...(category ? { category } : {}), ...(debounced ? { q: debounced } : {}) });
  const catalog = useAsync(
    (s) => {
      setExtra([]);
      return client.get<Catalog>(`/public/stores/${storeId}/catalog?${qs}`, s).then((c) => {
        setCursor(c.nextCursor);
        return c;
      });
    },
    [storeId, category, debounced],
    { enabled: !!storeId && (!searchMode || debounced.length > 0 || !!category) },
  );

  const items = useMemo(() => {
    const all = [...(catalog.data?.items ?? []), ...extra];
    return ageGate === 'confirmed' ? all : all.filter((p) => !p.isAgeRestricted);
  }, [catalog.data, extra, ageGate]);

  const loadMore = async () => {
    if (!cursor) return;
    const next = await client.get<Catalog>(`/public/stores/${storeId}/catalog?${qs}&cursor=${cursor}`);
    setExtra((e) => [...e, ...next.items]);
    setCursor(next.nextCursor);
  };

  if (!storeId) {
    return (
      <Page>
        <EmptyState icon={<Search className="h-8 w-8" />} title={t('home.chooseStore')} action={<Button onClick={() => nav('/')}>{t('cart.browse')}</Button>} />
      </Page>
    );
  }

  const visibleCategories = (categories.data ?? []).filter((c) => ageGate === 'confirmed' || c.productType !== 'ALCOHOL');
  return (
    <Page wide className="pt-0">
      <div className="relative -mx-4 h-[196px] overflow-hidden">
        <StorefrontArt name={store.data?.name ?? 'Runner Boy'} className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-night-950 via-night-950/55 to-night-950/10" />
        <button onClick={() => nav(-1)} className="rb-press rb-glass absolute left-4 top-3 grid h-10 w-10 place-items-center rounded-full text-white" aria-label={t('common.back')}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="absolute inset-x-4 bottom-4 text-white">
          {store.loading ? (
            <Skeleton className="h-14 bg-white/10" />
          ) : store.data ? (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                {store.data.sellerOfRecord.licenceVerified && (
                  <span className="rb-glass inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold">
                    <BadgeCheck className="h-3.5 w-3.5 text-marigold-500" aria-hidden /> {t('home.licensed', 'Licensed · verified')}
                  </span>
                )}
                <span className={cx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold', store.data.isOpen ? 'bg-success-500/90 text-white' : 'bg-danger-500/90 text-white')}>
                  <Clock className="h-3 w-3" aria-hidden /> {store.data.isOpen ? (store.data.closesAt ? t('home.closesAt', { time: store.data.closesAt }) : t('home.openNow')) : t('home.closed')}
                </span>
                {store.data.rating !== null && <span className="rb-num rounded-full bg-white px-2 py-1 text-[11px] font-bold text-ink-900">★ {store.data.rating.toFixed(1)}</span>}
              </div>
              <h1 className="rb-display mt-2 truncate text-[26px] font-bold leading-tight">{store.data.name}</h1>
              <div className="mt-0.5 flex items-center gap-1 truncate text-[12px] text-white/70">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-marigold-500" aria-hidden />
                <span className="truncate">{t('common.sellerOfRecord', { store: store.data.sellerOfRecord.legalName })}</span>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* One compact sticky row (~56 px): a search button inline with the aisle chips; it expands into the field. */}
      <div className="sticky z-20 -mx-4 border-b border-ink-900/[0.06] bg-paper/95 py-2 backdrop-blur-xl" style={{ top: 'var(--rb-header-h, 60px)' }}>
        {searchOpen ? (
          <div className="flex items-center gap-2 px-4">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-ink-400" aria-hidden />
              <Input
                value={q}
                onChange={(e) => updateQuery(e.target.value)}
                placeholder={t('catalog.searchIn', { store: store.data?.name ?? '' })}
                aria-label={t('common.search')}
                className="h-10 rounded-full pl-10 text-[14px] shadow-[var(--shadow-card)]"
                autoFocus
              />
            </div>
            <button type="button" onClick={closeSearch} className="rb-press h-10 shrink-0 rounded-full px-3.5 text-[13px] font-semibold text-ink-700" aria-label={t('common.close')}>
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <div ref={chipsRef} className="rb-scroll-x flex items-center gap-2 overflow-x-auto px-4">
            <button type="button" onClick={() => setSearchOpen(true)} className="rb-press grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink-900 text-white shadow-[var(--shadow-card)]" aria-label={t('common.search')}>
              <Search className="h-[18px] w-[18px]" />
            </button>
            {[{ slug: '', name: t('catalog.all') }, ...visibleCategories].map((c) => (
              <button
                key={c.slug || 'all'}
                onClick={() =>
                  setSp((p) => {
                    const n = new URLSearchParams(p);
                    if (c.slug) n.set('category', c.slug);
                    else n.delete('category');
                    return n;
                  })
                }
                aria-pressed={category === c.slug}
                className={cx('rb-press inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full pl-1 pr-3.5 text-[13px] font-semibold', category === c.slug ? 'bg-marigold-500 text-ink-950' : 'bg-white text-ink-700 shadow-[var(--shadow-card)]', !c.slug && 'pl-3.5')}
              >
                {c.slug && (
                  <span className="grid h-8 w-8 place-items-center rounded-full" style={stageStyle(c.slug)}>
                    <Packshot brand="" name={c.name} categorySlug={c.slug} volumeMl={null} className="h-6 w-6" />
                  </span>
                )}
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="h-3" aria-hidden />

      {browsingHome && ads.STORE_BANNER?.[0] && (
        <div className="mb-4">
          <AdBanner ad={ads.STORE_BANNER[0]} />
        </div>
      )}
      {!!category && ads.CATEGORY_BANNER?.[0] && (
        <div className="mb-4">
          <AdBanner ad={ads.CATEGORY_BANNER[0]} />
        </div>
      )}
      {browsingHome && ads.SPONSORED_PRODUCTS?.[0] && (
        <div className="mb-4">
          <SponsoredProducts ad={ads.SPONSORED_PRODUCTS[0]} storeId={storeId} />
        </div>
      )}

      {ageGate === 'declined' && (
        <Alert tone="info" className="mb-3">
          {t('catalog.alcoholHidden')}{' '}
          <button className="font-semibold underline" onClick={() => setAgeOpen(true)}>
            {t('common.change')}
          </button>
        </Alert>
      )}
      {catalog.data && !catalog.data.alcoholBrowsable && <Alert tone="warning" className="mb-3">{t('compliance.JURISDICTION_DISABLED')}</Alert>}

      {catalog.loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4]" />
          ))}
        </div>
      ) : searchMode && !debounced && !category ? (
        <EmptyState icon={<Search className="h-8 w-8" />} title={t('catalog.searchIn', { store: store.data?.name ?? '' })} body="Kingfisher · KF · whiskey · soda · ice" />
      ) : items.length === 0 ? (
        <EmptyState icon={<Search className="h-8 w-8" />} title={t('catalog.noResults', { query: debounced || category })} body={t('catalog.noResultsBody')} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((p) => (
              <ProductCard key={p.storeProductId} product={p} actions={actions} />
            ))}
          </div>
          {cursor && (
            <div className="mt-6 flex justify-center">
              <Button variant="outline" onClick={() => void loadMore()}>
                {t('catalog.loadMore')}
              </Button>
            </div>
          )}
          <p className="mt-4 text-center text-xs text-ink-500">{t('catalog.inclTaxes')}</p>
        </>
      )}
      <ResponsibleNote />
      <CartConflictSheet actions={actions} />
      <AgeGateSheet open={ageOpen} onDone={() => setAgeOpen(false)} />
    </Page>
  );
}
